import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';

import { downloadPreApprovalPdf, showHrRequest, updateRequestStatus } from '../../services/allRequestsApi';
import api from '../../services/api';
import { buildAttachmentUrl } from '../../config/env';
import { LoadingState, PageHeader, PageLayout, SectionCard, StatusBadge } from '../../components/ui';
import { getLocalEmployeeCode, getLocalRole, isHrTeamRole, normalizeDisplayValue } from './components/requestsUiUtils';
import WorkflowPathPreview from './components/WorkflowPathPreview';

/* ─────────────────────────────────────────────────────
 | Workflow state styles (unchanged)
 ──────────────────────────────────────────────────── */
function getWorkflowStateStyles(theme) {
  return {
    completed: {
      color: theme.palette.success.dark,
      bg: alpha(theme.palette.success.main, 0.14),
      label: 'Completed',
      icon: CheckCircleOutlineRoundedIcon,
    },
    current: {
      color: theme.palette.info.dark,
      bg: alpha(theme.palette.info.main, 0.14),
      label: 'In Progress',
      icon: PendingActionsRoundedIcon,
    },
    rejected: {
      color: theme.palette.error.dark,
      bg: alpha(theme.palette.error.main, 0.14),
      label: 'Rejected',
      icon: CancelOutlinedIcon,
    },
    waiting: {
      color: theme.palette.warning.dark,
      bg: alpha(theme.palette.warning.main, 0.16),
      label: 'Waiting',
      icon: HourglassBottomRoundedIcon,
    },
    blocked: {
      color: theme.palette.text.secondary,
      bg: alpha(theme.palette.text.secondary, 0.12),
      label: 'Blocked',
      icon: BlockRoundedIcon,
    },
  };
}

/* ─────────────────────────────────────────────────────
 | Workflow definitions (unchanged)
 ──────────────────────────────────────────────────── */
const REQUEST_WORKFLOWS = {
  to_leave: [
    { key: 'manager', label: 'Manager Review', owner: 'Manager', field: 'status' },
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
  ],
  misc: [
    { key: 'manager', label: 'Manager Review', owner: 'Manager', field: 'status' },
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
  ],
  business_trip: [
    { key: 'manager', label: 'Manager Review', owner: 'Manager', field: 'status' },
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
  ],
  resignation: [
    { key: 'manager', label: 'Manager Review', owner: 'Manager', field: 'status' },
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
  ],
  personal_data_change: [
    { key: 'manager', label: 'Manager Review', owner: 'Manager', field: 'status' },
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
  ],
  bank: [
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
  ],
  missing_punch: [
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
  ],
  finance_claim: [
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
    {
      key: 'finance_coordinator',
      label: 'Finance Coordinator Review',
      owner: 'Finance Coordinator',
      field: 'finance_coordinator_status',
    },
    { key: 'finance', label: 'Finance Approval', owner: 'Finance', field: 'finance_status' },
  ],
  loans: [
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
    { key: 'finance', label: 'Finance Review', owner: 'Finance', field: 'finance_status' },
    { key: 'ceo', label: 'CEO Final Approval', owner: 'CEO', field: 'ceo_status' },
  ],
  pre_approval: [
    {
      key: 'hr_admin',
      label: 'HR Manager Review',
      owner: 'HR Admin',
      field: 'hr_status',
    },
    {
      key: 'hod',
      label: 'Head of Department',
      owner: 'HOD',
      ownerNameField: 'pre_approval_hod_name',
      field: 'pre_approval_hod_status',
      actionByField: 'pre_approval_hod_action_by',
      actionByNameField: 'pre_approval_hod_action_by_name',
      actionAtField: 'pre_approval_hod_action_at',
    },
    {
      key: 'ops',
      label: 'Operations Manager',
      owner: 'Operations Manager',
      ownerNameField: 'pre_approval_operations_name',
      field: 'pre_approval_operations_status',
      actionByField: 'pre_approval_operations_action_by',
      actionByNameField: 'pre_approval_operations_action_by_name',
      actionAtField: 'pre_approval_operations_action_at',
    },
    {
      key: 'ceo',
      label: 'CEO Approval',
      owner: 'CEO',
      ownerNameField: 'pre_approval_ceo_name',
      field: 'pre_approval_ceo_status',
      actionByField: 'pre_approval_ceo_action_by',
      actionByNameField: 'pre_approval_ceo_action_by_name',
      actionAtField: 'pre_approval_ceo_action_at',
    },
    {
      key: 'exec_md',
      label: 'Executive Managing Director',
      owner: 'Executive MD',
      ownerNameField: 'pre_approval_executive_md_name',
      field: 'pre_approval_executive_md_status',
      actionByField: 'pre_approval_executive_md_action_by',
      actionByNameField: 'pre_approval_executive_md_action_by_name',
      actionAtField: 'pre_approval_executive_md_action_at',
    },
  ],
};

const HOD_ROLE_KEYS = ['hod', 'head_of_department', 'head_department', 'head_of_dept'];
const OPERATIONS_ROLE_KEYS = ['operation_manager', 'operations_manager', 'operations', 'operation'];
const EXECUTIVE_ROLE_KEYS = [
  'executive',
  'executive_manager',
  'executive_director',
  'executive_managing_director',
];

const PRE_APPROVAL_BILLING_OPTIONS = [
  { value: 'billed', label: 'Billed' },
  { value: 'lumpsum', label: 'Lumpsum' },
  { value: 'billing_hq', label: 'Billing HQ' },
];

const createReviewDialogState = () => ({
  open: false,
  action: 'approved',
  reason: '',
  revision_target_step: '',
  pre_approval_billing_mode: '',
  client_billing: '',
  marginal_benefit: '',
  missing_punch_effective_time: '',
  missing_punch_effective_project_id: '',
  missing_punch_hr_note: '',
});

function normalizePreApprovalRoutingValue(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function isBusinessDevelopmentDepartmentValue(value) {
  const normalized = normalizePreApprovalRoutingValue(value);
  if (!normalized) return false;
  return (
    ['bd', 'business_development', 'businessdevelopment'].includes(normalized) ||
    normalized.includes('business_development') ||
    normalized.includes('businessdevelopment')
  );
}

function shouldSkipPreApprovalHodStep(requestData) {
  if (requestData?.pre_approval_skip_hod != null) {
    return Boolean(requestData.pre_approval_skip_hod);
  }

  const hodCode = String(requestData?.pre_approval_hod_code || '').trim();
  const operationsCode = String(requestData?.pre_approval_operations_code || '').trim();
  if (hodCode && operationsCode && hodCode === operationsCode) {
    return true;
  }

  return (
    isBusinessDevelopmentDepartmentValue(requestData?.pre_approval_department_code) ||
    isBusinessDevelopmentDepartmentValue(requestData?.pre_approval_department_name)
  );
}

/* ─────────────────────────────────────────────────────
 | Pure helper functions (all unchanged)
 ──────────────────────────────────────────────────── */
function normalizeStatus(value) {
  const v = String(value ?? '').trim().toLowerCase();
  if (!v) return 'pending';
  if (v === 'approved' || v === 'rejected' || v === 'pending') return v;
  return 'pending';
}

function formatRequestType(type) {
  return String(type || 'N/A')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatStatusLabel(value, fallback = 'Pending') {
  const base = String(value || fallback)
    .replace(/_/g, ' ')
    .trim();
  return base ? base.replace(/\b\w/g, (c) => c.toUpperCase()) : fallback;
}

const isHodRole = (role) => HOD_ROLE_KEYS.includes(String(role || '').toLowerCase());
const isOperationsRole = (role) => OPERATIONS_ROLE_KEYS.includes(String(role || '').toLowerCase());
const isExecutiveRole = (role) => EXECUTIVE_ROLE_KEYS.includes(String(role || '').toLowerCase());
const normalizePreApprovalBillingMode = (value) => String(value || '').trim().toLowerCase();
const isBillingHqMode = (value) => normalizePreApprovalBillingMode(value) === 'billing_hq';
const normalizeMainTabFromQuery = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['assigned', 'assigned_requests', 'queue'].includes(normalized)) return 'assigned_requests';
  if (['archive', 'archive_requests', 'history'].includes(normalized)) return 'archive_requests';
  return 'my_requests';
};
const normalizeRequestTypeFromQuery = (value) => String(value || '').trim().toLowerCase() || 'all';
const normalizeRequestTypeToken = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
const requestMatchesType = (request, expectedType, phrase = '') => {
  const expected = normalizeRequestTypeToken(expectedType);
  const normalizedPhrase = String(phrase || expectedType || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');

  return [
    request?.request_type,
    request?.type,
    request?.type_key,
    request?.type_label,
    request?.type_detail,
    request?.reason_preview,
  ]
    .filter(Boolean)
    .some((value) => {
      const raw = String(value || '').trim().toLowerCase();
      const token = normalizeRequestTypeToken(raw);
      return token === expected || token.includes(expected) || (normalizedPhrase && raw.includes(normalizedPhrase));
    });
};
const isPreApprovalRequest = (request) => requestMatchesType(request, 'pre_approval', 'pre approval');
const isMissingPunchRequest = (request) =>
  requestMatchesType(request, 'missing_punch', 'missing punch') ||
  requestMatchesType(request, 'missing_punch', 'missed punch');
const normalizeId = (value) => String(value ?? '').trim();
const toTimeInputValue = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};
const getMissingPunchProject = (request, effective = false) =>
  effective
    ? request?.missing_punch_effective_project || request?.missingPunchEffectiveProject || null
    : request?.missing_punch_project || request?.missingPunchProject || null;
const getProjectLabel = (project) => {
  if (!project) return '';
  const code = project.code || project.project_code || project.projectCode;
  return [project.name, code ? `(${code})` : null].filter(Boolean).join(' ');
};
const getWorkflowTaskRowsForAction = (request) => {
  const rows = [];
  if (Array.isArray(request?.workflow_task_metadata)) rows.push(...request.workflow_task_metadata);
  if (Array.isArray(request?.workflow_tasks)) rows.push(...request.workflow_tasks);
  if (Array.isArray(request?.workflowTasks)) rows.push(...request.workflowTasks);
  if (Array.isArray(request?.workflow_instance?.tasks)) rows.push(...request.workflow_instance.tasks);
  if (Array.isArray(request?.workflowInstance?.tasks)) rows.push(...request.workflowInstance.tasks);
  return rows;
};
const getPendingWorkflowActionTask = (request) =>
  getWorkflowTaskRowsForAction(request).find(
    (task) => String(task?.status || '').toLowerCase() === 'pending' && (task?.task_id || task?.id)
  ) || null;
const hasWorkflowActionInstance = (request) =>
  Boolean(request?.workflow_instance || request?.workflowInstance || getWorkflowTaskRowsForAction(request).length);
const canRoleActOnWorkflowTask = (task, role, employeeCode) => {
  if (!task) return false;
  const assignedCode = String(
    task?.approver_employee_code ||
    task?.assigned_employee_code ||
    task?.assigned_user?.employee_code ||
    task?.assignedUser?.employee_code ||
    ''
  ).trim();
  if (assignedCode && String(employeeCode || '').trim() === assignedCode) return true;

  const roleKey = normalizePreApprovalRole(role);
  const taskRole = normalizePreApprovalRole(task?.approver_role || task?.role || task?.node_key || task?.nodeKey);
  if (!taskRole) return true;
  if (taskRole === roleKey) return true;
  if (['hr', 'hr_admin', 'hr_team'].includes(taskRole) && isHrTeamRole(roleKey)) return true;
  if (taskRole === 'hod' && isHodRole(roleKey)) return true;
  if (taskRole === 'operations_manager' && isOperationsRole(roleKey)) return true;
  if (taskRole === 'executive_managing_director' && isExecutiveRole(roleKey)) return true;
  return false;
};
const getWorkflowActionPayload = (request) => {
  const task = getPendingWorkflowActionTask(request);
  const taskId = task?.task_id || task?.id;
  return taskId ? { workflow_task_id: taskId } : {};
};
const resolveAssignedActionField = (request, role, employeeCode) => {
  if (!request) return null;

  const roleKey = normalizePreApprovalRole(role);
  const type = normalizeRequestTypeToken(request?.request_type);
  const managerStatus = String(request?.status || '').toLowerCase();
  const hrStatus = String(request?.hr_status || '').toLowerCase();
  const financeCoordStatus = String(request?.finance_coordinator_status || '').toLowerCase();
  const finStatus = String(request?.finance_status || '').toLowerCase();
  const ceoStatus = String(request?.ceo_status || '').toLowerCase();
  const preApprovalStatus = String(request?.pre_approval_status || '').toLowerCase();
  const preStep = String(request?.pre_approval_current_step || '').toLowerCase();
  const preHod = String(request?.pre_approval_hod_status || '').toLowerCase();
  const preOps = String(request?.pre_approval_operations_status || '').toLowerCase();
  const preCeo = String(request?.pre_approval_ceo_status || '').toLowerCase();
  const preExec = String(request?.pre_approval_executive_md_status || '').toLowerCase();

  if (hasWorkflowActionInstance(request)) {
    const task = getPendingWorkflowActionTask(request);
    return task && canRoleActOnWorkflowTask(task, roleKey, employeeCode) ? 'workflow_task' : null;
  }

  if (type === 'pre_approval' || isPreApprovalRequest(request)) {
    if (preApprovalStatus === 'revision_requested') return null;
    if (roleKey === 'hr_admin' && preStep === 'hr_admin' && hrStatus === 'pending') return 'hr_status';
    if (isHodRole(roleKey) && preStep === 'hod' && preHod === 'pending') return 'pre_approval_hod_status';
    if (isOperationsRole(roleKey) && preStep === 'operations_manager' && preOps === 'pending') {
      return 'pre_approval_operations_status';
    }
    if (roleKey === 'ceo' && preStep === 'ceo' && preCeo === 'pending') return 'pre_approval_ceo_status';
    if (isExecutiveRole(roleKey) && preStep === 'executive_managing_director' && preExec === 'pending') {
      return 'pre_approval_executive_md_status';
    }
    return null;
  }

  if (roleKey === 'manager' && managerStatus === 'pending') return 'status';

  if (roleKey === 'hr_admin') {
    const isDirectManager = String(request?.user?.manager_id || '') === String(employeeCode || '');
    if (isDirectManager && managerStatus === 'pending') return 'status';
    if (hrStatus === 'pending') return 'hr_status';
  }

  if (roleKey === 'finance_coordinator' && type === 'finance_claim' && financeCoordStatus === 'pending') {
    return 'finance_coordinator_status';
  }

  if (roleKey === 'finance') {
    if (
      type === 'finance_claim' &&
      hrStatus === 'approved' &&
      financeCoordStatus === 'approved' &&
      finStatus === 'pending'
    ) {
      return 'finance_status';
    }
    if (type === 'loans' && hrStatus === 'approved' && finStatus === 'pending') return 'finance_status';
  }

  if (roleKey === 'ceo' && type === 'loans' && finStatus === 'approved' && ceoStatus === 'pending') {
    return 'ceo_status';
  }

  return null;
};

function formatPercentageValue(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (raw.endsWith('%')) return raw;

  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return raw;

  const formatted = Number.isInteger(numeric)
    ? String(numeric)
    : numeric.toFixed(2).replace(/\.?0+$/, '');

  return `${formatted}%`;
}

function getPreApprovalBillingModeDisplay(requestData) {
  const billingMode = String(requestData?.pre_approval_billing_mode || '').toLowerCase();
  const billingModeLabel = String(requestData?.pre_approval_billing_mode_display || '').trim();

  if (billingMode === 'lumpsum') {
    const marginalBenefit = formatPercentageValue(requestData?.marginal_benefit);
    if (marginalBenefit) {
      return `${marginalBenefit} Lumpsum`;
    }
  }

  if (billingMode === 'billing_hq') {
    return billingModeLabel || 'Billing HQ';
  }

  return billingModeLabel || 'N/A';
}

function getPreApprovalBillingValueDisplay(requestData) {
  if (String(requestData?.pre_approval_billing_mode || '').toLowerCase() === 'billing_hq') {
    return 'N/A';
  }

  return requestData?.client_billing ?? 'N/A';
}

function getPreApprovalMarginDisplay(requestData) {
  if (String(requestData?.pre_approval_billing_mode || '').toLowerCase() === 'billing_hq') {
    return 'N/A';
  }

  return formatPercentageValue(requestData?.marginal_benefit) || 'N/A';
}

function formatHumanDateTime(value, { includeTime = true } = {}) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  try {
    return parsed.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
  } catch {
    return trimmed;
  }
}

function formatDateTime(date, time) {
  if (!date && !time) return 'N/A';
  const formattedDate = date ? formatHumanDateTime(date, { includeTime: false }) : '';
  if (formattedDate && time) return `${formattedDate} (${time})`;
  return formattedDate || time || 'N/A';
}

function formatAuditDateTime(value) {
  const formatted = formatHumanDateTime(value);
  return formatted || 'N/A';
}

function getRevisionStatusLabel(status) {
  return String(status || '').toLowerCase() === 'resubmitted'
    ? 'Revision Resubmitted'
    : 'Revision Requested';
}

function hasDisplayValue(value) {
  return String(value ?? '').trim() !== '';
}

function getPrimaryText(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
}

function getPreApprovalRevisionHistory(requestData) {
  if (Array.isArray(requestData?.pre_approval_revision_history) && requestData.pre_approval_revision_history.length) {
    return requestData.pre_approval_revision_history;
  }

  const hasSnapshot =
    hasDisplayValue(requestData?.pre_approval_revision_reason) ||
    hasDisplayValue(requestData?.pre_approval_revision_requested_at) ||
    hasDisplayValue(requestData?.pre_approval_revision_target_step) ||
    hasDisplayValue(requestData?.pre_approval_revision_requested_by_step) ||
    hasDisplayValue(requestData?.pre_approval_revision_resolved_at);

  if (!hasSnapshot) return [];

  return [
    {
      id: 'latest-snapshot',
      requested_by_step: requestData?.pre_approval_revision_requested_by_step,
      requested_by_name: requestData?.pre_approval_revision_requested_by_name,
      requested_by_label: requestData?.pre_approval_revision_requested_by_label,
      target_step: requestData?.pre_approval_revision_target_step,
      target_label: requestData?.pre_approval_revision_target_label,
      reason: requestData?.pre_approval_revision_reason,
      status: requestData?.pre_approval_revision_resolved_at ? 'resubmitted' : 'requested',
      requested_at: requestData?.pre_approval_revision_requested_at,
      resolved_at: requestData?.pre_approval_revision_resolved_at,
      resolved_by_name: requestData?.pre_approval_revision_resolved_by_name,
    },
  ];
}

function getLatestPreApprovalRevision(requestData) {
  const history = getPreApprovalRevisionHistory(requestData);
  return history.length ? history[0] : null;
}

function getPreApprovalRevisionTargetDetails(requestData) {
  const targetStep = String(requestData?.pre_approval_revision_target_step || '').toLowerCase();
  const targetLabel = String(requestData?.pre_approval_revision_target_label || '').trim();
  const targetName =
    targetStep === 'hr_team_creator'
      ? getPrimaryText(requestData?.user?.name, requestData?.requester_name, requestData?.prepared_by_name)
      : targetStep === 'hod'
        ? getPrimaryText(requestData?.pre_approval_hod_name)
        : '';

  let display = targetLabel || 'N/A';
  if (targetName && targetLabel) {
    display = `${targetName} (${targetLabel})`;
  } else if (targetName) {
    display = targetName;
  }

  return {
    step: targetStep,
    label: targetLabel,
    name: targetName,
    display,
  };
}

function getPreApprovalWorkflowStepKey(stepKey) {
  return (
    {
      hr_admin: 'hr_admin',
      hod: 'hod',
      ops: 'operations_manager',
      ceo: 'ceo',
      exec_md: 'executive_managing_director',
    }[String(stepKey || '').toLowerCase()] || String(stepKey || '').toLowerCase()
  );
}

function getRequestReasonInfo(requestData) {
  const requestType = String(requestData?.request_type || '').toLowerCase();

  switch (requestType) {
    case 'to_leave':
      return {
        label: 'Leave Reason',
        value: getPrimaryText(requestData?.reason, requestData?.description, requestData?.notes),
      };
    case 'misc':
      return {
        label: 'Request Reason',
        value: getPrimaryText(requestData?.misc_reason, requestData?.description, requestData?.notes),
      };
    case 'business_trip':
      return {
        label: 'Trip Purpose',
        value: getPrimaryText(requestData?.description, requestData?.notes),
      };
    case 'resignation':
      return {
        label: 'Resignation Reason',
        value: getPrimaryText(requestData?.resignation_reason, requestData?.description, requestData?.notes),
      };
    case 'personal_data_change':
      return {
        label: 'Change Reason',
        value: getPrimaryText(requestData?.description, requestData?.notes),
      };
    case 'finance_claim':
      return {
        label: 'Claim Context',
        value: getPrimaryText(requestData?.description, requestData?.notes),
      };
    case 'missing_punch':
      return {
        label: 'Missing Punch Reason',
        value: getPrimaryText(requestData?.description, requestData?.notes),
      };
    case 'loans':
      return {
        label: 'Loan Purpose',
        value: getPrimaryText(requestData?.description, requestData?.notes),
      };
    case 'pre_approval':
      return {
        label: 'Business Context',
        value: getPrimaryText(requestData?.description, requestData?.notes, requestData?.conditional_terms),
      };
    default:
      return {
        label: 'Request Reason',
        value: getPrimaryText(requestData?.reason, requestData?.description, requestData?.notes),
      };
  }
}

function getSupportingContext(requestData, primaryReason) {
  const candidates = [
    { label: 'Description', value: requestData?.description },
    { label: 'Notes', value: requestData?.notes },
  ];

  return candidates.filter((item) => {
    const value = String(item.value ?? '').trim();
    return value && value !== primaryReason;
  });
}

function normalizePreApprovalRole(role) {
  return String(role || '').toLowerCase().trim().replace(/[\s-]+/g, '_');
}

function getPreApprovalEditWindow(requestData, currentRole, currentEmployeeCode) {
  if (String(requestData?.request_type || '').toLowerCase() !== 'pre_approval') {
    return null;
  }

  const role = normalizePreApprovalRole(currentRole);
  const employeeCode = String(currentEmployeeCode || '');
  const skipHod = shouldSkipPreApprovalHodStep(requestData);
  const rawCurrentStep = String(requestData?.pre_approval_current_step || '').toLowerCase();
  const currentStep = skipHod && rawCurrentStep === 'hod' ? 'operations_manager' : rawCurrentStep;
  const currentStatus = String(requestData?.pre_approval_status || '').toLowerCase();
  const hrStatus = normalizeStatus(requestData?.hr_status);
  const hodStatus = normalizeStatus(requestData?.pre_approval_hod_status);
  const operationsStatus = normalizeStatus(requestData?.pre_approval_operations_status);
  const ceoStatus = normalizeStatus(requestData?.pre_approval_ceo_status);
  const executiveStatus = normalizeStatus(requestData?.pre_approval_executive_md_status);
  const isCreator = String(requestData?.user?.employee_code || '') === employeeCode;
  const isHod = String(requestData?.pre_approval_hod_code || '') === employeeCode;
  const isOperations = String(requestData?.pre_approval_operations_code || '') === employeeCode;
  const isCeo = String(requestData?.pre_approval_ceo_code || '') === employeeCode;
  const isExecutive = String(requestData?.pre_approval_executive_md_code || '') === employeeCode;

  if (currentStatus === 'revision_requested') {
    const revisionTarget = String(requestData?.pre_approval_revision_target_step || '').toLowerCase();
    if (revisionTarget === 'hr_team_creator' && isCreator && isHrTeamRole(currentRole)) return 'hr_team_creator';
    if (!skipHod && revisionTarget === 'hod' && isHod) return 'hod';
    return null;
  }

  if (isCreator && isHrTeamRole(currentRole) && currentStep === 'hr_admin' && hrStatus === 'pending') {
    return 'hr_team_creator';
  }

  if (
    ['hr_admin', 'admin_hr'].includes(role) &&
    ((currentStep === 'hr_admin' && hrStatus === 'pending') ||
      (!skipHod && currentStep === 'hod' && hrStatus === 'approved' && hodStatus === 'pending') ||
      (skipHod && currentStep === 'operations_manager' && hrStatus === 'approved' && operationsStatus === 'pending'))
  ) {
    return 'hr_admin';
  }

  if (
    !skipHod &&
    isHod &&
    ((currentStep === 'hod' && hodStatus === 'pending') ||
      (currentStep === 'operations_manager' && hodStatus === 'approved' && operationsStatus === 'pending'))
  ) {
    return 'hod';
  }

  if (
    isOperations &&
    ((currentStep === 'operations_manager' && operationsStatus === 'pending') ||
      (currentStep === 'ceo' && operationsStatus === 'approved' && ceoStatus === 'pending'))
  ) {
    return 'operations_manager';
  }

  if (
    isCeo &&
    ((currentStep === 'ceo' && ceoStatus === 'pending') ||
      (currentStep === 'executive_managing_director' && ceoStatus === 'approved' && executiveStatus === 'pending'))
  ) {
    return 'ceo';
  }

  if (isExecutive && currentStep === 'executive_managing_director' && executiveStatus === 'pending') {
    return 'executive_managing_director';
  }

  return null;
}

// Mirrors the backend isUserActivePreApprovalRevisionTarget: the person a
// parked revision is assigned to. Besides Edit & Resubmit, they may reject the
// request outright (with a reason, e.g. the client turned the candidate down)
// while it waits for revision — approvals stay blocked until resubmission.
function isPreApprovalRevisionAssignee(requestData, currentRole, currentEmployeeCode) {
  if (String(requestData?.request_type || '').toLowerCase() !== 'pre_approval') return false;
  if (String(requestData?.pre_approval_status || '').toLowerCase() !== 'revision_requested') return false;

  const role = normalizePreApprovalRole(currentRole);
  const employeeCode = String(currentEmployeeCode || '').trim();
  const target = String(requestData?.pre_approval_revision_target_step || '').toLowerCase();

  if (target === 'hr_team_creator') {
    return String(requestData?.user?.employee_code || '') === employeeCode && isHrTeamRole(currentRole);
  }
  if (target === 'hr_admin') return ['hr_admin', 'admin_hr'].includes(role);
  if (!employeeCode) return false;
  if (target === 'hod') {
    return !shouldSkipPreApprovalHodStep(requestData) &&
      String(requestData?.pre_approval_hod_code || '') === employeeCode;
  }
  if (target === 'operations_manager' || target === 'operation_manager') {
    return String(requestData?.pre_approval_operations_code || '') === employeeCode;
  }
  if (target === 'ceo') return String(requestData?.pre_approval_ceo_code || '') === employeeCode;
  if (target === 'executive_managing_director' || target === 'executive') {
    return String(requestData?.pre_approval_executive_md_code || '') === employeeCode;
  }
  return false;
}

function getFinalStatus(r) {
  if (!r) return 'Pending';
  if (String(r.request_type || '').toLowerCase() === 'pre_approval') {
    const status = String(r.pre_approval_status || '').toLowerCase();
    if (status === 'revision_requested') return 'Revision';
    if (status === 'fully_approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    return 'Pending';
  }
  const chain = [
    r.status,
    r.hr_status,
    r.finance_coordinator_status,
    r.finance_status,
    r.ceo_status,
  ].filter((v) => v != null && String(v).trim() !== '');

  if (!chain.length) return 'Pending';
  const upper = chain.map((v) => String(v).toUpperCase());
  if (upper.includes('REJECTED')) return 'Rejected';
  if (upper.every((v) => v === 'APPROVED')) return 'Approved';
  return 'Pending';
}

function getWorkflowDefinition(type) {
  const requestType = String(type || '').toLowerCase();
  return REQUEST_WORKFLOWS[requestType] || [
    { key: 'hr', label: 'HR Review', owner: 'HR Admin', field: 'hr_status' },
  ];
}

function buildWorkflowSteps(requestData) {
  const baseDefinition = getWorkflowDefinition(requestData?.request_type);
  const definition =
    String(requestData?.request_type || '').toLowerCase() === 'pre_approval' && shouldSkipPreApprovalHodStep(requestData)
      ? baseDefinition.filter((step) => step.key !== 'hod')
      : baseDefinition;
  const revisionHistory = getPreApprovalRevisionHistory(requestData);
  const base = definition.map((step, idx) => {
    const rawStatus = requestData?.[step.field];
    const status = normalizeStatus(rawStatus);
    const revisionEntry =
      String(requestData?.request_type || '').toLowerCase() === 'pre_approval'
        ? revisionHistory.find(
            (item) =>
              String(item?.requested_by_step || '').toLowerCase() === getPreApprovalWorkflowStepKey(step.key)
          ) || null
        : null;
    return {
      ...step,
      index: idx + 1,
      rawStatus,
      status,
      ownerName: step.ownerNameField ? requestData?.[step.ownerNameField] : null,
      actionBy: step.actionByField ? requestData?.[step.actionByField] : null,
      actionByName: step.actionByNameField ? requestData?.[step.actionByNameField] : null,
      actionAt: step.actionAtField ? requestData?.[step.actionAtField] : null,
      revisionEntry,
    };
  });

  return base.map((step, idx) => {
    const previousSteps = base.slice(0, idx);
    const hasRejectedBefore = previousSteps.some((p) => p.status === 'rejected');
    const allPreviousApproved = previousSteps.every((p) => p.status === 'approved');

    let state = 'waiting';
    if (hasRejectedBefore) state = 'blocked';
    else if (step.status === 'approved') state = 'completed';
    else if (step.status === 'rejected') state = 'rejected';
    else if (allPreviousApproved) state = 'current';

    return {
      ...step,
      state,
      revisionLabel: step.revisionEntry ? getRevisionStatusLabel(step.revisionEntry.status) : null,
      revisionAt: step.revisionEntry
        ? (String(step.revisionEntry.status || '').toLowerCase() === 'resubmitted'
            ? step.revisionEntry.resolved_at || step.revisionEntry.requested_at
            : step.revisionEntry.requested_at)
        : null,
      revisionTargetLabel: step.revisionEntry?.target_label || null,
      revisionReason: step.revisionEntry?.reason || null,
    };
  });
}

function getWorkflowHeadline(steps) {
  const rejected = steps.find((s) => s.state === 'rejected');
  if (rejected) return `Stopped at ${rejected.label}`;

  const current = steps.find((s) => s.state === 'current');
  if (current) return `Awaiting ${current.owner}`;

  return 'Workflow completed';
}

/* ─────────────────────────────────────────────────────
 | WorkflowStep — vertical timeline row
 ──────────────────────────────────────────────────── */
function WorkflowStep({ step, isLast }) {
  const theme = useTheme();
  const STYLES = getWorkflowStateStyles(theme);
  const stateStyle = STYLES[step.state] || STYLES.waiting;
  const Icon = stateStyle.icon;
  const isCurrent = step.state === 'current';

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {/* Timeline spine */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 30, flexShrink: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            backgroundColor: stateStyle.bg,
            color: stateStyle.color,
            border: `2px solid ${alpha(stateStyle.color, 0.5)}`,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            zIndex: 1,
            boxShadow: isCurrent ? `0 0 0 4px ${alpha(stateStyle.color, 0.14)}` : 'none',
            transition: 'box-shadow 0.2s',
          }}
        >
          <Icon sx={{ fontSize: 15 }} />
        </Box>
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              minHeight: 20,
              mt: 0.5,
              backgroundColor: alpha(theme.palette.text.secondary, 0.15),
              borderRadius: 99,
            }}
          />
        )}
      </Box>

      {/* Step card */}
      <Box sx={{ pb: isLast ? 0 : 2, flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            p: { xs: 1.25, sm: 1.5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: isCurrent ? alpha(stateStyle.color, 0.35) : 'divider',
            backgroundColor: isCurrent
              ? alpha(stateStyle.color, 0.05)
              : alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.65 : 0.88),
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: stateStyle.color,
                  fontSize: '0.67rem',
                }}
              >
                Step {step.index}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.15, lineHeight: 1.35 }}>
                {step.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                {step.ownerName || step.owner}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.4, flexShrink: 0 }}>
              <StatusBadge status={step.rawStatus || 'pending'} />
              <Typography
                variant="caption"
                sx={{
                  color: stateStyle.color,
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  backgroundColor: stateStyle.bg,
                  borderRadius: 999,
                  px: 0.8,
                  py: 0.15,
                }}
              >
                {stateStyle.label}
              </Typography>
            </Box>
          </Box>

          {(step.actionByName || step.actionBy || step.actionAt) ? (
            <Box
              sx={{
                mt: 1,
                pt: 0.75,
                borderTop: '1px dashed',
                borderColor: alpha(theme.palette.text.secondary, 0.2),
                display: 'flex',
                gap: 0.5,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {step.actionByName || step.actionBy || '—'}
              </Typography>
              {step.actionAt && (
                <Typography variant="caption" color="text.disabled">
                  · {step.actionAt}
                </Typography>
              )}
            </Box>
          ) : null}
          {step.revisionLabel ? (
            <Box
              sx={{
                mt: 1,
                pt: 0.75,
                borderTop: '1px dashed',
                borderColor: alpha(theme.palette.warning.main, 0.22),
              }}
            >
              <Stack spacing={0.35}>
                <Typography variant="caption" sx={{ color: theme.palette.warning.dark, fontWeight: 800 }}>
                  {step.revisionLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.revisionTargetLabel ? `Returned To: ${step.revisionTargetLabel}` : 'Returned for revision'}
                  {step.revisionAt ? ` · ${formatAuditDateTime(step.revisionAt)}` : ''}
                </Typography>
                {step.revisionReason ? (
                  <Typography variant="caption" color="text.secondary">
                    Revision Reason: {step.revisionReason}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────
 | InfoField — label + value display cell
 ──────────────────────────────────────────────────── */
function InfoField({ label, value, children, fullWidth = false }) {
  const theme = useTheme();
  const content = children ?? value;
  const isEmpty = !children && (value === null || value === undefined || String(value).trim() === '');

  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Box
        sx={{
          minHeight: 74,
          p: 1.25,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: isEmpty ? alpha('#9db2c8', 0.2) : alpha('#9db2c8', 0.32),
          backgroundColor: alpha('#ffffff', theme.palette.mode === 'dark' ? 0.05 : 0.72),
          boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 8px 18px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            fontWeight: 800,
            fontSize: '0.67rem',
            // Use the theme's semantic secondary color so the label stays
            // legible in both light and dark mode. The previous fixed navy
            // disappeared against the dark card background.
            color: isEmpty ? 'text.disabled' : 'text.secondary',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 0.2,
            fontWeight: isEmpty ? 400 : 700,
            color: isEmpty ? 'text.disabled' : theme.palette.mode === 'dark' ? 'text.primary' : '#102653',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.5,
          }}
        >
          {isEmpty ? '—' : content}
        </Typography>
      </Box>
    </Grid>
  );
}

/* ─────────────────────────────────────────────────────
 | Main page component
 ──────────────────────────────────────────────────── */
function ShowHrRequestPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(createReviewDialogState);
  const [missingPunchProjects, setMissingPunchProjects] = useState([]);
  const [missingPunchProjectsLoading, setMissingPunchProjectsLoading] = useState(false);
  const currentRole = getLocalRole();
  const currentEmployeeCode = getLocalEmployeeCode();
  const skipPreApprovalHod = shouldSkipPreApprovalHodStep(requestData);
  const actionField = useMemo(
    () => resolveAssignedActionField(requestData, currentRole, currentEmployeeCode),
    [currentEmployeeCode, currentRole, requestData]
  );
  const hasAssignedAction = Boolean(actionField);
  const reviewRequest = requestData;
  const reviewIsPreApproval = isPreApprovalRequest(reviewRequest);
  const reviewIsMissingPunch = isMissingPunchRequest(reviewRequest);
  const reviewCurrentStep = String(reviewRequest?.pre_approval_current_step || '').toLowerCase();
  const reviewBillingMode = normalizePreApprovalBillingMode(reviewDialog.pre_approval_billing_mode);
  const reviewIsBillingHq = reviewBillingMode === 'billing_hq';
  const reviewIsHodApproval = reviewDialog.action === 'approved' && reviewCurrentStep === 'hod';
  const submittedMissingPunchProject = getMissingPunchProject(reviewRequest);
  const effectiveMissingPunchProject = getMissingPunchProject(reviewRequest, true);
  const submittedMissingPunchProjectId = normalizeId(
    reviewRequest?.missing_punch_project_id ?? submittedMissingPunchProject?.id
  );
  const submittedMissingPunchTime = toTimeInputValue(reviewRequest?.missing_punch_time);
  const effectiveMissingPunchTime = toTimeInputValue(
    reviewDialog.missing_punch_effective_time ||
      reviewRequest?.missing_punch_effective_time ||
      reviewRequest?.missing_punch_time
  );
  const effectiveMissingPunchProjectId = normalizeId(
    reviewDialog.missing_punch_effective_project_id ||
      reviewRequest?.missing_punch_effective_project_id ||
      reviewRequest?.missing_punch_project_id ||
      submittedMissingPunchProject?.id
  );
  const missingPunchTimeChanged =
    reviewIsMissingPunch && effectiveMissingPunchTime && submittedMissingPunchTime
      ? effectiveMissingPunchTime !== submittedMissingPunchTime
      : false;
  const missingPunchProjectChanged =
    reviewIsMissingPunch && effectiveMissingPunchProjectId !== submittedMissingPunchProjectId;
  const missingPunchHrNoteRequired =
    reviewIsMissingPunch &&
    reviewDialog.action === 'approved' &&
    (missingPunchTimeChanged || missingPunchProjectChanged);
  const missingPunchProjectOptions = useMemo(() => {
    const options = Array.isArray(missingPunchProjects) ? [...missingPunchProjects] : [];
    [submittedMissingPunchProject, effectiveMissingPunchProject].forEach((project) => {
      if (!project?.id) return;
      if (!options.some((option) => normalizeId(option.id) === normalizeId(project.id))) {
        options.unshift(project);
      }
    });
    return options;
  }, [effectiveMissingPunchProject, missingPunchProjects, submittedMissingPunchProject]);
  const selectedMissingPunchProject = useMemo(
    () =>
      missingPunchProjectOptions.find(
        (project) => normalizeId(project.id) === effectiveMissingPunchProjectId
      ) || null,
    [effectiveMissingPunchProjectId, missingPunchProjectOptions]
  );
  const reviewTargetOptions = useMemo(() => {
    if (!reviewIsPreApproval) return [];
    if (['operations_manager', 'ceo', 'executive_managing_director'].includes(reviewCurrentStep)) {
      return [
        { value: 'hr_team_creator', label: 'HR Team Creator' },
        { value: 'hod', label: 'Head of Department' },
      ];
    }

    return [{ value: 'hr_team_creator', label: 'HR Team Creator' }];
  }, [reviewCurrentStep, reviewIsPreApproval]);
  const reviewComputedMargin = useMemo(() => {
    if (reviewDialog.pre_approval_billing_mode !== 'billed') {
      return reviewDialog.marginal_benefit;
    }

    const totalCost = Number(reviewRequest?.total_cost_to_company || 0);
    const clientBilling = Number(reviewDialog.client_billing || 0);
    if (!Number.isFinite(totalCost) || !Number.isFinite(clientBilling) || clientBilling <= 0) {
      return '';
    }

    return ((1 - totalCost / clientBilling) * 100).toFixed(2);
  }, [
    reviewDialog.client_billing,
    reviewDialog.marginal_benefit,
    reviewDialog.pre_approval_billing_mode,
    reviewRequest?.total_cost_to_company,
  ]);
  const backTarget = useMemo(() => {
    const explicitReturn = location.state?.returnTo;
    if (typeof explicitReturn === 'string' && explicitReturn.startsWith('/')) {
      return explicitReturn;
    }

    const params = new URLSearchParams(location.search);
    const sourceView = normalizeMainTabFromQuery(
      params.get('from') || params.get('view') || (hasAssignedAction ? 'assigned_requests' : 'my_requests')
    );
    const requestType = normalizeRequestTypeFromQuery(
      params.get('request_type') || params.get('type') || requestData?.request_type
    );
    const listParams = new URLSearchParams();
    if (sourceView !== 'my_requests') listParams.set('view', sourceView);
    if (requestType && requestType !== 'all') listParams.set('request_type', requestType);
    const query = listParams.toString();
    return `/all-requests${query ? `?${query}` : ''}`;
  }, [hasAssignedAction, location.search, location.state, requestData?.request_type]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await showHrRequest(id);
        if (isMounted) setRequestData(data);
      } catch (err) {
        console.error('Error fetching request:', err);
        if (isMounted) setError('Could not load request details. Please try again.');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [id]);

  useEffect(() => {
    if (!reviewDialog.open || !reviewIsMissingPunch) return undefined;

    let mounted = true;
    setMissingPunchProjectsLoading(true);
    api.get('/projects')
      .then((res) => {
        if (!mounted) return;
        setMissingPunchProjects(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        console.error('Failed to fetch projects for missing punch action', err);
        if (mounted) setMissingPunchProjects([]);
      })
      .finally(() => {
        if (mounted) setMissingPunchProjectsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [reviewDialog.open, reviewIsMissingPunch]);

  const refreshRequest = useCallback(async () => {
    const data = await showHrRequest(id);
    setRequestData(data);
    return data;
  }, [id]);

  const closeReviewDialog = useCallback(() => {
    setReviewDialog(createReviewDialogState());
  }, []);

  const openReviewDialog = useCallback((action) => {
    if (!requestData) return;
    const submittedProject = getMissingPunchProject(requestData);
    const effectiveProject = getMissingPunchProject(requestData, true);
    setReviewDialog({
      open: true,
      action,
      reason: '',
      revision_target_step: action === 'revised' ? 'hr_team_creator' : '',
      pre_approval_billing_mode: String(requestData?.pre_approval_billing_mode || '').trim(),
      client_billing: requestData?.client_billing != null ? String(requestData.client_billing) : '',
      marginal_benefit: requestData?.marginal_benefit != null ? String(requestData.marginal_benefit) : '',
      missing_punch_effective_time: toTimeInputValue(
        requestData?.missing_punch_effective_time || requestData?.missing_punch_time
      ),
      missing_punch_effective_project_id: normalizeId(
        requestData?.missing_punch_effective_project_id ||
          effectiveProject?.id ||
          requestData?.missing_punch_project_id ||
          submittedProject?.id
      ),
      missing_punch_hr_note: String(requestData?.missing_punch_hr_note || ''),
    });
  }, [requestData]);

  const submitSimpleAction = useCallback(async (action) => {
    if (!requestData?.id || updating) return;

    setUpdating(true);
    setError(null);
    try {
      await updateRequestStatus(requestData.id, action, getWorkflowActionPayload(requestData));
      await refreshRequest();
      if (typeof window.refreshAssignedPendingCount === 'function') {
        window.refreshAssignedPendingCount();
      }
    } catch (err) {
      console.error('Failed to update request status:', err);
      setError(err?.response?.data?.message || 'Failed to update request status.');
    } finally {
      setUpdating(false);
    }
  }, [refreshRequest, requestData, updating]);

  const handleAssignedAction = useCallback((action) => {
    if (!requestData?.id || !actionField) return;

    if (reviewIsPreApproval || reviewIsMissingPunch) {
      openReviewDialog(action);
      return;
    }

    submitSimpleAction(action);
  }, [
    actionField,
    openReviewDialog,
    requestData?.id,
    reviewIsMissingPunch,
    reviewIsPreApproval,
    submitSimpleAction,
  ]);

  const submitReviewDialog = useCallback(async () => {
    if (!reviewRequest?.id || updating) return;

    const payload = {};
    const trimmedReason = String(reviewDialog.reason || '').trim();
    Object.assign(payload, getWorkflowActionPayload(reviewRequest));

    if (reviewDialog.action === 'rejected') {
      if (!trimmedReason) {
        setError('Rejection reason is required.');
        return;
      }
      payload.rejection_reason = trimmedReason;
    }

    if (reviewIsMissingPunch) {
      const hrNote = String(reviewDialog.missing_punch_hr_note || '').trim();

      if (reviewDialog.action === 'approved') {
        const effectiveTime = effectiveMissingPunchTime || submittedMissingPunchTime;
        if (!effectiveTime) {
          setError('Effective punch time is required before approval.');
          return;
        }
        if (missingPunchHrNoteRequired && !hrNote) {
          setError('HR note is required when changing the submitted punch time or project.');
          return;
        }

        payload.missing_punch_effective_time = effectiveTime;
        if (effectiveMissingPunchProjectId) {
          payload.missing_punch_effective_project_id = effectiveMissingPunchProjectId;
        }
      }

      if (hrNote) {
        payload.missing_punch_hr_note = hrNote;
      }
    }

    if (reviewDialog.action === 'revised') {
      if (!reviewDialog.revision_target_step) {
        setError('Revision target is required.');
        return;
      }
      if (!trimmedReason) {
        setError('Revision reason is required.');
        return;
      }
      payload.revision_target_step = reviewDialog.revision_target_step;
      payload.revision_reason = trimmedReason;
    }

    if (reviewIsHodApproval) {
      const billingMode = String(reviewDialog.pre_approval_billing_mode || '').trim();
      const clientBilling = Number(reviewDialog.client_billing || 0);
      const manualMargin = String(reviewDialog.marginal_benefit || '').trim();

      if (!billingMode) {
        setError('Billing mode is required for HOD approval.');
        return;
      }
      if (!isBillingHqMode(billingMode) && (!Number.isFinite(clientBilling) || clientBilling <= 0)) {
        setError('Billing value must be greater than zero.');
        return;
      }
      if (billingMode === 'lumpsum' && !manualMargin) {
        setError('Margin benefit is required for lumpsum billing.');
        return;
      }

      payload.pre_approval_billing_mode = billingMode;
      payload.client_billing = isBillingHqMode(billingMode) ? null : reviewDialog.client_billing;
      payload.marginal_benefit = isBillingHqMode(billingMode)
        ? null
        : billingMode === 'billed'
          ? reviewComputedMargin
          : manualMargin;
    }

    setUpdating(true);
    setError(null);
    try {
      await updateRequestStatus(reviewRequest.id, reviewDialog.action, payload);
      closeReviewDialog();
      await refreshRequest();
      if (typeof window.refreshAssignedPendingCount === 'function') {
        window.refreshAssignedPendingCount();
      }
    } catch (err) {
      console.error('Failed to submit request action:', err);
      setError(err?.response?.data?.message || 'Failed to update request status.');
    } finally {
      setUpdating(false);
    }
  }, [
    closeReviewDialog,
    effectiveMissingPunchProjectId,
    effectiveMissingPunchTime,
    missingPunchHrNoteRequired,
    refreshRequest,
    reviewComputedMargin,
    reviewDialog.action,
    reviewDialog.client_billing,
    reviewDialog.marginal_benefit,
    reviewDialog.missing_punch_hr_note,
    reviewDialog.pre_approval_billing_mode,
    reviewDialog.reason,
    reviewDialog.revision_target_step,
    reviewIsHodApproval,
    reviewIsMissingPunch,
    reviewRequest,
    submittedMissingPunchTime,
    updating,
  ]);

  const handleDownloadPreApprovalPdf = async () => {
    try {
      const blob = await downloadPreApprovalPdf(id, true);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pre_approval_${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download pre-approval PDF', err);
      setError('Failed to download PDF. Please try again.');
    }
  };

  const handleOpenPreApprovalPdf = async () => {
    try {
      const blob = await downloadPreApprovalPdf(id, false);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open pre-approval PDF', err);
      setError('Failed to open PDF. Please try again.');
    }
  };

  const headerActions = useMemo(() => {
    const actions = [
      <Button
        key="back"
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(backTarget)}
        sx={{
          minHeight: 42,
          borderColor: alpha('#1f3d7c', 0.28),
          color: '#102653',
          fontWeight: 850,
        }}
      >
        Back
      </Button>,
    ];

    if (hasAssignedAction) {
      actions.unshift(
        <ButtonGroup key="assigned-actions" variant="contained" disabled={updating} sx={{ '& .MuiButton-root': { minHeight: 42, fontWeight: 850, textTransform: 'none' } }}>
          <Button
            color="success"
            startIcon={<CheckCircleOutlineRoundedIcon />}
            onClick={() => handleAssignedAction('approved')}
          >
            Approve
          </Button>
          {isPreApprovalRequest(requestData) ? (
            <Button
              color="primary"
              startIcon={<PendingActionsRoundedIcon />}
              onClick={() => handleAssignedAction('revised')}
            >
              Revise
            </Button>
          ) : null}
          <Button
            color="error"
            startIcon={<CancelOutlinedIcon />}
            onClick={() => handleAssignedAction('rejected')}
          >
            Reject
          </Button>
        </ButtonGroup>,
      );
    }

    // A parked revision hides the normal Approve/Revise/Reject strip, but the
    // revision assignee may still reject the request outright (with a reason)
    // instead of resubmitting it — approvals stay blocked until resubmission.
    if (!hasAssignedAction && isPreApprovalRevisionAssignee(requestData, currentRole, currentEmployeeCode)) {
      actions.unshift(
        <Button
          key="revision-reject"
          variant="contained"
          color="error"
          disabled={updating}
          startIcon={<CancelOutlinedIcon />}
          onClick={() => openReviewDialog('rejected')}
          sx={{ minHeight: 42, fontWeight: 850, textTransform: 'none' }}
        >
          Reject
        </Button>,
      );
    }

    const canEditPreApproval = Boolean(
      requestData?.request_type === 'pre_approval' &&
      getPreApprovalEditWindow(requestData, currentRole, currentEmployeeCode)
    );

    if (canEditPreApproval) {
      actions.unshift(
        <Button
          key="edit"
          variant="contained"
          onClick={() => navigate(`/hr-requests/${id}/edit`)}
          sx={{
            minHeight: 42,
            px: 2.2,
            fontWeight: 900,
            background: 'linear-gradient(135deg, #214a9a 0%, #0f9cc2 100%)',
            boxShadow: '0 12px 24px rgba(15, 117, 217, 0.22)',
          }}
        >
          {requestData?.pre_approval_status === 'revision_requested' ? 'Edit & Resubmit' : 'Edit Request'}
        </Button>,
      );
    }

    return actions;
  }, [
    backTarget,
    currentEmployeeCode,
    currentRole,
    handleAssignedAction,
    hasAssignedAction,
    id,
    navigate,
    openReviewDialog,
    requestData,
    updating,
  ]);

  /* ── Loading / Error / Not found ── */
  if (loading) {
    return (
      <PageLayout backgroundAccent maxWidth="xl">
        <PageHeader overline="Requests" title={`Request #${id}`} actions={headerActions} compact />
        <LoadingState rows={7} header={false} />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout backgroundAccent maxWidth="xl">
        <PageHeader overline="Requests" title={`Request #${id}`} actions={headerActions} compact />
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </PageLayout>
    );
  }

  if (!requestData) {
    return (
      <PageLayout backgroundAccent maxWidth="xl">
        <PageHeader overline="Requests" title={`Request #${id}`} actions={headerActions} compact />
        <Alert severity="warning" sx={{ borderRadius: 2 }}>Request not found.</Alert>
      </PageLayout>
    );
  }

  /* ── Computed values ── */
  const typeLabel = formatRequestType(requestData.request_type);
  const finalStatus = getFinalStatus(requestData);
  const workflowSteps = buildWorkflowSteps(requestData);
  const workflowEngineInstance = requestData.workflow_instance || requestData.workflowInstance || null;
  const workflowEngineTaskRows =
    workflowEngineInstance?.tasks ||
    requestData.workflow_tasks ||
    requestData.workflowTasks ||
    [];
  const workflowEngineTasks = Array.isArray(workflowEngineTaskRows) ? workflowEngineTaskRows : [];
  const workflowEngineEventRows = workflowEngineInstance?.events || [];
  const workflowEngineEvents = Array.isArray(workflowEngineEventRows) ? workflowEngineEventRows : [];
  const hasWorkflowEngineInstance = Boolean(workflowEngineInstance?.id);
  const workflowHeadline = getWorkflowHeadline(workflowSteps);
  const completedCount = workflowSteps.filter((s) => s.status === 'approved').length;
  const currentStep = workflowSteps.find((s) => s.state === 'current');
  const currentWorkflowTask = workflowEngineTasks.find((task) => String(task?.status || '').toLowerCase() === 'pending');
  const workflowEngineCompletedCount = workflowEngineTasks.filter(
    (task) => String(task?.status || '').toLowerCase() === 'approved',
  ).length;
  const workflowProgressCompleted = hasWorkflowEngineInstance ? workflowEngineCompletedCount : completedCount;
  const workflowProgressTotal = hasWorkflowEngineInstance
    ? Math.max(workflowEngineTasks.length, currentWorkflowTask ? workflowEngineCompletedCount + 1 : workflowEngineCompletedCount, 1)
    : workflowSteps.length;
  const progressPct = workflowProgressTotal > 0
    ? Math.round((workflowProgressCompleted / workflowProgressTotal) * 100)
    : 0;
  const workflowProgressHeadline = hasWorkflowEngineInstance
    ? (currentWorkflowTask?.node_label
      ? `Waiting for ${currentWorkflowTask.node_label}`
      : `Workflow instance is ${formatStatusLabel(workflowEngineInstance?.status, 'active')}`)
    : workflowHeadline;
  const waitingForDisplay = hasWorkflowEngineInstance
    ? (
      currentWorkflowTask?.assigned_user?.name ||
      currentWorkflowTask?.assignedUser?.name ||
      currentWorkflowTask?.approver_employee_code ||
      formatStatusLabel(currentWorkflowTask?.approver_role, '')
    )
    : (currentStep?.ownerName || currentStep?.owner);
  const requestDate = formatAuditDateTime(requestData.request_date || requestData.created_at);
  const reasonInfo = getRequestReasonInfo(requestData);
  const supportingContext = getSupportingContext(requestData, reasonInfo.value);
  const revisionHistory = getPreApprovalRevisionHistory(requestData);
  const latestRevision = getLatestPreApprovalRevision(requestData);
  const revisionTargetDetails = getPreApprovalRevisionTargetDetails(requestData);
  const activePreApprovalRevision =
    requestData.request_type === 'pre_approval' &&
    String(requestData.pre_approval_status || '').toLowerCase() === 'revision_requested';
  const isCurrentUserRevisionTarget =
    activePreApprovalRevision &&
    Boolean(getPreApprovalEditWindow(requestData, currentRole, currentEmployeeCode));
  const workflowRejectionReasons = [
    { label: 'Manager Rejection Reason', value: requestData.manager_rejection_reason },
    { label: 'HR Rejection Reason', value: requestData.hr_rejection_reason },
    { label: 'Finance Coordinator Rejection Reason', value: requestData.finance_coordinator_rejection_reason },
    { label: 'Finance Rejection Reason', value: requestData.finance_rejection_reason },
    { label: 'CEO Rejection Reason', value: requestData.ceo_rejection_reason },
  ].filter((item) => hasDisplayValue(item.value));
  const preApprovalRejectionReasons = [
    { label: 'HOD Rejection Reason', value: requestData.pre_approval_hod_rejection_reason },
    { label: 'Operations Rejection Reason', value: requestData.pre_approval_operations_rejection_reason },
    { label: 'CEO Rejection Reason', value: requestData.pre_approval_ceo_rejection_reason },
    { label: 'Executive MD Rejection Reason', value: requestData.pre_approval_executive_md_rejection_reason },
  ].filter((item) => hasDisplayValue(item.value));
  const allRejectionReasons = [...workflowRejectionReasons, ...preApprovalRejectionReasons];
  const highlightedDecisionNotes = (() => {
    const notes = [];

    if (hasDisplayValue(latestRevision?.reason)) {
      const requestedBy =
        latestRevision?.requested_by_label ||
        formatStatusLabel(latestRevision?.requested_by_step, '');
      const caption = [
        requestedBy,
        latestRevision?.target_label ? `Target: ${latestRevision.target_label}` : '',
        latestRevision?.requested_at ? `Requested ${formatAuditDateTime(latestRevision.requested_at)}` : '',
      ]
        .filter(Boolean)
        .join(' · ');

      notes.push({
        key: 'revision',
        tone: 'warning',
        label:
          String(latestRevision?.status || '').toLowerCase() === 'resubmitted'
            ? 'Latest Revision Note'
            : 'Revision Reason',
        value: latestRevision.reason,
        caption,
      });
    }

    if (allRejectionReasons.length > 0) {
      const primaryRejectionReason = allRejectionReasons[0];
      notes.push({
        key: 'rejection',
        tone: 'error',
        label: primaryRejectionReason.label,
        value: primaryRejectionReason.value,
        caption: 'Captured from the latest rejection step.',
      });
    }

    return notes;
  })();

  /* Status color */
  const statusColorMap = {
    Approved: theme.palette.success,
    Rejected: theme.palette.error,
    Revision: theme.palette.info,
    Pending: theme.palette.warning,
  };
  const statusPalette = statusColorMap[finalStatus] || theme.palette.warning;
  const StatusIcon = finalStatus === 'Approved'
    ? CheckCircleRoundedIcon
    : finalStatus === 'Rejected'
      ? CancelRoundedIcon
      : finalStatus === 'Revision'
        ? PendingActionsRoundedIcon
      : HourglassBottomRoundedIcon;
  const requestReference = requestData.pre_approval_reference || requestData.reference || `#${id}`;
  const shellBorder = alpha('#9db2c8', dark ? 0.28 : 0.36);
  const panelSx = {
    borderRadius: '14px',
    borderColor: shellBorder,
    background:
      `linear-gradient(180deg, ${alpha('#ffffff', dark ? 0.08 : 0.98)} 0%, ${alpha('#f7fbff', dark ? 0.05 : 0.94)} 100%)`,
    boxShadow: dark ? 'none' : '0 16px 38px rgba(15, 23, 42, 0.07)',
    overflow: 'hidden',
  };
  const requestSummaryTiles = [
    {
      label: 'Request Type',
      value: typeLabel,
      icon: <LabelRoundedIcon fontSize="small" />,
      color: '#0f94bf',
    },
    {
      label: 'Requester',
      value: requestData.user?.name || requestData.requester_name || 'N/A',
      icon: <PersonRoundedIcon fontSize="small" />,
      color: '#1f3d7c',
    },
    {
      label: 'Employee Code',
      value: requestData.user?.employee_code || currentEmployeeCode || 'N/A',
      icon: <BadgeRoundedIcon fontSize="small" />,
      color: '#80a936',
    },
    {
      label: 'Submitted',
      value: requestDate,
      icon: <CalendarTodayRoundedIcon fontSize="small" />,
      color: '#d58a1f',
    },
  ];

  /* ── Render ── */
  return (
    <PageLayout backgroundAccent maxWidth={false}>
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', xl: 1720 },
          mx: 'auto',
          fontFamily: '"Manrope", "Segoe UI", "Helvetica Neue", sans-serif',
          '@keyframes requestDetailRise': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
      <Box
        sx={{
          mb: 2.5,
          p: { xs: 1.6, md: 2.2 },
          borderRadius: '16px',
          border: '1px solid',
          borderColor: shellBorder,
          background:
            `linear-gradient(135deg, ${alpha('#ffffff', dark ? 0.08 : 0.98)} 0%, ${alpha('#f6fbff', dark ? 0.05 : 0.96)} 48%, ${alpha('#edf7fd', dark ? 0.07 : 0.94)} 100%)`,
          boxShadow: dark ? 'none' : '0 18px 46px rgba(15, 23, 42, 0.09)',
          position: 'relative',
          overflow: 'hidden',
          animation: 'requestDetailRise 260ms ease both',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: dark ? 0.26 : 0.58,
            pointerEvents: 'none',
            display: { xs: 'none', md: 'block' },
          }}
        >
          <svg viewBox="0 0 1160 240" width="100%" height="100%" preserveAspectRatio="none">
            <path
              d="M34 138 C126 190, 241 164, 345 104 C437 51, 515 47, 609 92 C701 136, 755 176, 849 102 C936 34, 1000 81, 1065 132 C1099 159, 1126 145, 1145 122"
              fill="none"
              stroke="#1aa4c8"
              strokeWidth="3"
              strokeDasharray="10 14"
              strokeLinecap="round"
            />
            <path d="M407 166 L612 54 L822 158 Z" fill="none" stroke="#d8e8f3" strokeWidth="1" />
            <circle cx="606" cy="92" r="8" fill="#0d75d9" opacity="0.82" />
            <circle cx="851" cy="101" r="8" fill="#22a35a" opacity="0.82" />
            <circle cx="991" cy="93" r="8" fill="#f0a329" opacity="0.82" />
          </svg>
        </Box>

        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(420px, 0.82fr)' },
            gap: { xs: 1.8, lg: 2.4 },
            alignItems: 'center',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.9} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip
                size="small"
                label={typeLabel}
                sx={{
                  fontWeight: 850,
                  color: '#0f6c91',
                  borderColor: alpha('#0f94bf', 0.28),
                  backgroundColor: alpha('#0f94bf', 0.1),
                }}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`Ref ${requestReference}`}
                sx={{
                  fontWeight: 850,
                  color: '#1f3d7c',
                  borderColor: alpha('#1f3d7c', 0.22),
                  backgroundColor: alpha('#1f3d7c', 0.08),
                }}
                variant="outlined"
              />
              <Chip
                size="small"
                label={finalStatus}
                sx={{
                  fontWeight: 850,
                  color: statusPalette.dark,
                  borderColor: alpha(statusPalette.main, 0.28),
                  backgroundColor: alpha(statusPalette.main, 0.12),
                }}
                variant="outlined"
              />
              {requestData.priority && requestData.request_type !== 'pre_approval' ? (
                <Chip size="small" label={`Priority: ${requestData.priority}`} sx={{ fontWeight: 850 }} variant="outlined" />
              ) : null}
            </Stack>
            <Typography
              component="h1"
              sx={{
                color: dark ? 'text.primary' : '#102653',
                fontSize: { xs: '2rem', md: '2.55rem' },
                lineHeight: 1.04,
                fontWeight: 950,
                letterSpacing: 0,
                mb: 0.75,
              }}
            >
              Request #{id}
            </Typography>
            <Typography sx={{ color: dark ? 'text.secondary' : alpha('#102653', 0.72), fontWeight: 650, maxWidth: 680 }}>
              {`${typeLabel} submitted ${requestDate}. Review the request fields, supporting documents, and approval journey in one workspace.`}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.25,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: shellBorder,
              backgroundColor: alpha('#ffffff', dark ? 0.07 : 0.92),
              boxShadow: dark ? 'none' : '0 16px 34px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Stack spacing={1.2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                {headerActions}
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 0.85,
                }}
              >
                {requestSummaryTiles.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1,
                      minHeight: 78,
                      borderRadius: '12px',
                      border: `1px solid ${alpha(item.color, 0.18)}`,
                      backgroundColor: alpha(item.color, dark ? 0.1 : 0.065),
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '10px',
                        color: item.color,
                        backgroundColor: alpha(item.color, 0.12),
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: dark ? 'text.secondary' : '#64748b', fontWeight: 850 }}>
                        {item.label}
                      </Typography>
                      <Typography
                        sx={{
                          color: dark ? 'text.primary' : '#102653',
                          fontWeight: 950,
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          overflowWrap: 'anywhere',
                          whiteSpace: 'normal',
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
      {false && <PageHeader
        variant="dashboard"
        compact
        overline="Request Details"
        title={`Request #${id}`}
        subtitle={`${typeLabel} · submitted ${requestDate}`}
        actions={headerActions}
      />}

      {/* Metadata chips strip */}
      {false && <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 2,
          p: { xs: 0.9, sm: 1 },
          flexWrap: 'wrap',
          gap: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, dark ? 0.7 : 0.9),
          backgroundColor: alpha(theme.palette.background.paper, dark ? 0.72 : 0.94),
          boxShadow: theme.shadows[1],
        }}
      >
        <Chip
          icon={<LabelRoundedIcon sx={{ fontSize: 15 }} />}
          label={typeLabel}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        <Chip
          icon={<PersonRoundedIcon sx={{ fontSize: 15 }} />}
          label={requestData.user?.name || '—'}
          size="small"
          variant="outlined"
        />
        <Chip
          icon={<BadgeRoundedIcon sx={{ fontSize: 15 }} />}
          label={requestData.user?.employee_code || '—'}
          size="small"
          variant="outlined"
        />
        <Chip
          icon={<CalendarTodayRoundedIcon sx={{ fontSize: 15 }} />}
          label={requestDate}
          size="small"
          variant="outlined"
        />
        {requestData.priority && requestData.request_type !== 'pre_approval' && (
          <Chip label={`Priority: ${requestData.priority}`} size="small" variant="outlined" />
        )}
        {requestData.request_type === 'pre_approval' && requestData.pre_approval_reference && (
          <Chip label={`Ref: ${requestData.pre_approval_reference}`} size="small" variant="outlined" />
        )}
      </Stack>}

      {/* Workspace layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) minmax(320px, 360px)',
            xl: 'minmax(0, 1fr) minmax(390px, 430px)',
          },
          gap: { xs: 2, md: 2.5, xl: 3 },
          alignItems: 'start',
        }}
      >

        {/* ════════ LEFT: main content ════════ */}
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={2.5}>

            {activePreApprovalRevision && latestRevision && (
              <Alert
                severity={isCurrentUserRevisionTarget ? 'warning' : 'info'}
                sx={{ borderRadius: 3, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
              >
                <Stack spacing={1.15}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {isCurrentUserRevisionTarget
                      ? 'This request was returned to you for revision.'
                      : 'This request was returned for revision.'}
                  </Typography>
                  <Grid container spacing={2}>
                    <InfoField
                      label="Requested By"
                      value={latestRevision.requested_by_name || latestRevision.requested_by_label}
                    />
                    <InfoField
                      label="Returned To"
                      value={revisionTargetDetails.display}
                    />
                    <InfoField
                      label="Requested At"
                      value={formatAuditDateTime(latestRevision.requested_at)}
                    />
                  </Grid>
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: alpha(theme.palette.warning.main, 0.24),
                      backgroundColor: alpha(theme.palette.warning.main, dark ? 0.1 : 0.06),
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem' }}
                    >
                      Revision Reason
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.55, whiteSpace: 'pre-wrap', fontWeight: 600 }}>
                      {latestRevision.reason || 'No revision reason was recorded.'}
                    </Typography>
                  </Box>
                </Stack>
              </Alert>
            )}

            {/* Reason / Context */}
            {(reasonInfo.value || supportingContext.length > 0) && (
              <Box
                sx={{
                  p: { xs: 1.75, sm: 2.25 },
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, 0.18),
                  background: dark
                    ? `linear-gradient(160deg, ${alpha(theme.palette.primary.dark, 0.22)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
                    : `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, fontSize: '0.67rem' }}
                >
                  {reasonInfo.label}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 0.75, whiteSpace: 'pre-wrap', lineHeight: 1.75, fontWeight: 450 }}
                >
                  {reasonInfo.value || 'No reason was recorded for this request.'}
                </Typography>

                {supportingContext.length > 0 && (
                  <Stack spacing={1.25} sx={{ mt: 1.75 }}>
                    {supportingContext.map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          pl: 1.5,
                          borderLeft: '3px solid',
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, fontSize: '0.67rem' }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.2, whiteSpace: 'pre-wrap' }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            {/* ── Request Details (type-specific) ── */}
            <SectionCard
              tone="dashboard"
              title="Request Details"
              subheader={`Submitted fields for this ${typeLabel.toLowerCase()} request`}
              sx={panelSx}
            >

              {/* To Leave */}
              {requestData.request_type === 'to_leave' && (
                <Grid container spacing={2}>
                  <InfoField label="Leave Type" value={requestData.leave_type} />
                  <InfoField label="From" value={formatDateTime(requestData.from_date, requestData.from_time)} />
                  <InfoField label="To" value={formatDateTime(requestData.to_date, requestData.to_time)} />
                  <InfoField label="Reason" value={requestData.reason} />
                </Grid>
              )}

              {/* Loans */}
              {requestData.request_type === 'loans' && (
                <Grid container spacing={2}>
                  <InfoField label="Loan Date" value={requestData.loan_date} />
                  <InfoField label="No. of Payments" value={requestData.no_of_payments} />
                  <InfoField label="Amount" value={requestData.amount} />
                </Grid>
              )}

              {/* Finance Claim */}
              {requestData.request_type === 'finance_claim' && (
                <Box>
                  {Array.isArray(requestData.finance_claim_items) && requestData.finance_claim_items.length > 0 ? (
                    <Stack spacing={1.5}>
                      {requestData.finance_claim_items.map((item, index) => (
                        <Box
                          key={item.id || index}
                          sx={{
                            p: 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.background.paper, dark ? 0.6 : 0.85),
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 1, display: 'block' }}>
                            Item {index + 1}
                          </Typography>
                          <Grid container spacing={2}>
                            <InfoField label="Transaction Date" value={item.transaction_date} />
                            <InfoField label="Expense Type" value={item.expense_type} />
                            <InfoField label="Amount" value={item.amount} />
                            <InfoField label="Notes" value={item.notes} />
                            {item.attachment_path && (
                              <InfoField label="Attachment">
                                <Link href={buildAttachmentUrl(item.attachment_path)} target="_blank" rel="noopener" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                                  <AttachFileRoundedIcon sx={{ fontSize: 14 }} /> View
                                </Link>
                              </InfoField>
                            )}
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No finance claim items found.</Typography>
                  )}
                </Box>
              )}

              {/* Misc */}
              {requestData.request_type === 'misc' && (
                <Grid container spacing={2}>
                  <InfoField label="Requested Item" value={requestData.requested_item} />
                  <InfoField label="Is Temporary?" value={requestData.is_temporary ? 'Yes' : 'No'} />
                  {requestData.is_temporary && (
                    <>
                      <InfoField label="Temporary From" value={formatDateTime(requestData.misc_from_date, requestData.misc_from_time)} />
                      <InfoField label="Temporary To" value={formatDateTime(requestData.misc_to_date, requestData.misc_to_time)} />
                    </>
                  )}
                  <InfoField label="Reason" value={requestData.misc_reason} />
                  <InfoField label="Notes" value={requestData.misc_notes} />
                </Grid>
              )}

              {/* Business Trip */}
              {requestData.request_type === 'business_trip' && (
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <InfoField label="Ticket / Accommodation" value={requestData.business_trip_ticket_demand} />
                    <InfoField label="Trip Type" value={requestData.business_trip_type} />
                    <InfoField label="Travel Method" value={requestData.business_trip_travel_method} />
                    <InfoField label="Destination" value={requestData.business_trip_destination} />
                    <InfoField label="Region" value={requestData.business_trip_region} />
                    <InfoField label="From" value={formatDateTime(requestData.from_date, requestData.from_time)} />
                    <InfoField label="To" value={formatDateTime(requestData.to_date, requestData.to_time)} />
                  </Grid>

                  {Array.isArray(requestData.attachments) && requestData.attachments.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75, display: 'block' }}>
                        Attachments
                      </Typography>
                      <Stack spacing={0.5}>
                        {requestData.attachments.map((file, index) => (
                          <Link
                            key={file.id || index}
                            href={buildAttachmentUrl(file.file_path)}
                            target="_blank"
                            rel="noopener"
                            underline="hover"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '0.875rem' }}
                          >
                            <AttachFileRoundedIcon sx={{ fontSize: 15 }} />
                            Attachment {index + 1}
                          </Link>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              )}

              {/* Missing Punch */}
              {requestData.request_type === 'missing_punch' && (
                <Grid container spacing={2}>
                  <InfoField label="Punch Date" value={requestData.missing_punch_date} />
                  <InfoField label="Punch Type" value={requestData.missing_punch_type} />
                  <InfoField label="Punch Time" value={requestData.missing_punch_time} />
                  <InfoField
                    label="Project"
                    value={
                      requestData.missing_punch_project?.name ||
                      requestData.missingPunchProject?.name ||
                      requestData.missing_punch_project_id
                    }
                  />
                  <InfoField label="Effective Time" value={requestData.missing_punch_effective_time} />
                  <InfoField
                    label="Effective Project"
                    value={
                      requestData.missing_punch_effective_project?.name ||
                      requestData.missingPunchEffectiveProject?.name ||
                      requestData.missing_punch_effective_project_id
                    }
                  />
                  <InfoField label="HR Note" value={requestData.missing_punch_hr_note} fullWidth />
                </Grid>
              )}

              {/* Bank */}
              {requestData.request_type === 'bank' && (
                <Grid container spacing={2}>
                  <InfoField label="Transaction Date" value={requestData.bank_transaction_date} />
                  <InfoField label="Bank Name" value={requestData.bank_name} />
                  <InfoField label="Branch" value={requestData.bank_branch} />
                  <InfoField label="Account Number" value={requestData.new_account_number} />
                  <InfoField label="IBAN Number" value={requestData.new_iban_number} />
                  <InfoField label="SWIFT Code" value={requestData.swift_code} />
                  <InfoField label="Notes" value={requestData.bank_notes} fullWidth />
                </Grid>
              )}

              {/* Personal Data Change */}
              {requestData.request_type === 'personal_data_change' && (
                <Grid container spacing={2}>
                  <InfoField label="Field to Change" value={requestData.personal_data_field} />
                  <InfoField label="New Value" value={requestData.personal_data_value} />
                  {requestData.transaction_date && (
                    <InfoField label="Transaction Date" value={requestData.transaction_date} />
                  )}
                </Grid>
              )}

              {/* Resignation */}
              {requestData.request_type === 'resignation' && (
                <Grid container spacing={2}>
                  <InfoField label="Resignation Date" value={requestData.resignation_date} />
                  <InfoField label="Last Working Day" value={requestData.last_working_day} />
                  <InfoField label="Notice Period" value={requestData.notice_period} />
                  <InfoField label="Reason" value={requestData.resignation_reason} fullWidth />
                </Grid>
              )}

              {/* Pre-Approval */}
              {requestData.request_type === 'pre_approval' && (
                <Stack spacing={2.5}>
                  {/* Candidate & Position */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem', mb: 1, display: 'block' }}>
                      Candidate Info
                    </Typography>
                    <Grid container spacing={2}>
                      <InfoField label="Candidate Name" value={requestData.candidate_name} />
                      <InfoField label="Position Offered" value={requestData.position_offered} />
                      <InfoField label="Project / Client" value={requestData.project_client} />
                      <InfoField label="Department" value={requestData.pre_approval_department_name} />
                    </Grid>
                  </Box>

                  {/* Financials */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem', mb: 1, display: 'block' }}>
                      Financials
                    </Typography>
                    <Grid container spacing={2}>
                      <InfoField label="Package Salary" value={requestData.package_salary} />
                      <InfoField label="Other Cost" value={requestData.other_cost} />
                      <InfoField label="Total Cost to Company" value={requestData.total_cost_to_company} />
                      <InfoField label="Billing Mode" value={getPreApprovalBillingModeDisplay(requestData)} />
                      <InfoField
                        label={requestData.pre_approval_billing_mode === 'lumpsum' ? 'Lumpsum Amount' : 'Client Billing'}
                        value={getPreApprovalBillingValueDisplay(requestData)}
                      />
                      <InfoField label="Marginal Benefit" value={getPreApprovalMarginDisplay(requestData)} />
                    </Grid>
                  </Box>

                  {/* Terms & Governance */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem', mb: 1, display: 'block' }}>
                      Terms & Governance
                    </Typography>
                    <Grid container spacing={2}>
                      <InfoField label="Conditional Terms" value={requestData.conditional_terms} fullWidth />
                      <InfoField label="Conflict of Interest" value={requestData.conflict_of_interest} fullWidth />
                    </Grid>
                  </Box>

                  {/* Approvers */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem', mb: 1, display: 'block' }}>
                      Assigned Approvers
                    </Typography>
                    <Grid container spacing={2}>
                      <InfoField label="Requester" value={requestData.requester_name} />
                      <InfoField label="Prepared By" value={requestData.prepared_by_name} />
                      {!skipPreApprovalHod ? (
                        <InfoField label="Assigned HOD" value={requestData.pre_approval_hod_name} />
                      ) : null}
                      <InfoField label="Operations Manager" value={requestData.pre_approval_operations_name} />
                      <InfoField label="CEO" value={requestData.pre_approval_ceo_name} />
                      <InfoField label="Executive MD" value={requestData.pre_approval_executive_md_name} />
                    </Grid>
                  </Box>

                  {/* Revision info */}
                  {latestRevision && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem', mb: 1, display: 'block' }}>
                        Latest Revision
                      </Typography>
                      <Grid container spacing={2}>
                        <InfoField label="Status" value={getRevisionStatusLabel(latestRevision.status)} />
                        <InfoField
                          label="Requested By Step"
                          value={latestRevision.requested_by_label || formatStatusLabel(latestRevision.requested_by_step, 'N/A')}
                        />
                        <InfoField label="Requested By" value={latestRevision.requested_by_name} />
                        <InfoField label="Returned To" value={revisionTargetDetails.display} />
                        <InfoField label="Requested At" value={formatAuditDateTime(latestRevision.requested_at)} />
                        <InfoField label="Resubmitted At" value={formatAuditDateTime(latestRevision.resolved_at)} />
                        <InfoField label="Revision Reason" value={latestRevision.reason} fullWidth />
                      </Grid>
                    </Box>
                  )}
                </Stack>
              )}
            </SectionCard>

            {/* ── Administrative Summary ── */}
            <SectionCard
              tone="dashboard"
              title="Administrative"
              subheader="Supporting info, scheduling, and documents"
              sx={panelSx}
            >
              <Grid container spacing={2}>
                {requestData.request_type !== 'pre_approval' && (
                  <InfoField label="Priority" value={requestData.priority || 'None'} />
                )}
                <InfoField label="Due Date" value={requestData.due_date} />
                <InfoField label="Notes" value={requestData.notes} fullWidth />
                {requestData.amount != null && String(requestData.amount) !== '' && (
                  <InfoField label="Amount" value={requestData.amount} />
                )}
                {requestData.attachment_path && (
                  <InfoField label="Attachment">
                    <Link
                      href={buildAttachmentUrl(requestData.attachment_path)}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '0.875rem' }}
                    >
                      <AttachFileRoundedIcon sx={{ fontSize: 15 }} /> View Attachment
                    </Link>
                  </InfoField>
                )}
              </Grid>

              {requestData.request_type === 'pre_approval' && requestData.pre_approval_status === 'fully_approved' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={handleDownloadPreApprovalPdf}>
                    Download Approved PDF
                  </Button>
                  <Button variant="outlined" onClick={handleOpenPreApprovalPdf}>
                    Open / Print PDF
                  </Button>
                </Stack>
              )}
            </SectionCard>

            {/* ── Decision Notes (rejection reasons) ── */}
            {allRejectionReasons.length > 0 && (
              <Box
                sx={{
                  p: { xs: 1.75, sm: 2.25 },
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: alpha(theme.palette.error.main, 0.25),
                  backgroundColor: alpha(theme.palette.error.main, dark ? 0.07 : 0.04),
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: 'error.dark' }}>
                  Decision Notes
                </Typography>
                <Grid container spacing={2}>
                  {allRejectionReasons.map((item) => (
                    <InfoField key={item.label} label={item.label} value={item.value} fullWidth />
                  ))}
                </Grid>
              </Box>
            )}

            {requestData.request_type === 'pre_approval' && revisionHistory.length > 0 && (
              <SectionCard
                title="Revision History"
                subtitle="Every revision cycle recorded for this pre-approval request"
                sx={panelSx}
              >
                <Stack spacing={1.5}>
                  {revisionHistory.map((item, index) => (
                    <Box
                      key={item.id || `revision-${index}`}
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.warning.main, 0.18),
                        backgroundColor: alpha(theme.palette.warning.main, dark ? 0.08 : 0.04),
                      }}
                    >
                      <Grid container spacing={2}>
                        <InfoField label="Status" value={getRevisionStatusLabel(item.status)} />
                        <InfoField
                          label="Requested By Step"
                          value={item.requested_by_label || formatStatusLabel(item.requested_by_step, 'N/A')}
                        />
                        <InfoField label="Requested By" value={item.requested_by_name} />
                        <InfoField label="Returned To" value={item.target_label} />
                        <InfoField label="Requested At" value={formatAuditDateTime(item.requested_at)} />
                        <InfoField label="Resolved / Resubmitted At" value={formatAuditDateTime(item.resolved_at)} />
                        <InfoField label="Resolved By" value={item.resolved_by_name} />
                        <InfoField label="Revision Reason" value={item.reason} fullWidth />
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </SectionCard>
            )}

          </Stack>
        </Box>

        {/* ════════ RIGHT: status + workflow sidebar ════════ */}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
            <Stack spacing={2}>

              {/* Status Card */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: '14px',
                  border: '1px solid',
                  borderColor: alpha(statusPalette.main, dark ? 0.3 : 0.24),
                  background:
                    `linear-gradient(180deg, ${alpha('#ffffff', dark ? 0.08 : 0.98)} 0%, ${alpha(statusPalette.main, dark ? 0.12 : 0.055)} 100%)`,
                  boxShadow: dark ? 'none' : '0 16px 38px rgba(15, 23, 42, 0.07)',
                }}
              >
                {/* Status header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '14px',
                      backgroundColor: alpha(statusPalette.main, 0.16),
                      color: statusPalette.dark,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <StatusIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem' }}>
                      Overall Status
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: statusPalette.dark, mt: 0.1, lineHeight: 1.2 }}>
                      {finalStatus}
                    </Typography>
                  </Box>
                </Box>

                {/* Progress */}
                <Stack spacing={0.75}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Approval progress
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {workflowProgressCompleted} / {workflowProgressTotal}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progressPct}
                    color={finalStatus === 'Rejected' ? 'error' : finalStatus === 'Approved' ? 'success' : 'primary'}
                    sx={{ height: 7, borderRadius: 99, backgroundColor: alpha(statusPalette.main, 0.12) }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <RouteRoundedIcon sx={{ fontSize: 13 }} />
                    {workflowProgressHeadline}
                  </Typography>
                </Stack>

                {/* Current owner */}
                {waitingForDisplay && (
                  <Box
                    sx={{
                      mt: 1.75,
                      pt: 1.5,
                      borderTop: '1px dashed',
                      borderColor: alpha(theme.palette.text.secondary, 0.18),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem' }}>
                      Waiting for
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.2 }}>
                      {waitingForDisplay}
                    </Typography>
                  </Box>
                )}

                {highlightedDecisionNotes.length > 0 && (
                  <Box
                    sx={{
                      mt: 1.75,
                      pt: 1.5,
                      borderTop: '1px dashed',
                      borderColor: alpha(theme.palette.text.secondary, 0.18),
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700, fontSize: '0.67rem' }}
                    >
                      Decision Notes
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 0.9 }}>
                      {highlightedDecisionNotes.map((note) => {
                        const palette = theme.palette[note.tone] || theme.palette.info;

                        return (
                          <Box
                            key={note.key}
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: alpha(palette.main, 0.24),
                              backgroundColor: alpha(palette.main, dark ? 0.1 : 0.06),
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontWeight: 800,
                                color: palette.dark,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                              }}
                            >
                              {note.label}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600, whiteSpace: 'pre-wrap' }}>
                              {note.value}
                            </Typography>
                            {note.caption ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.55 }}>
                                {note.caption}
                              </Typography>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>

              {hasWorkflowEngineInstance ? (
                <WorkflowPathPreview
                  title="Approval Journey"
                  subheader="Published workflow path and condition decisions for the frozen version used by this request."
                  instance={workflowEngineInstance}
                  tasks={workflowEngineTasks}
                  events={workflowEngineEvents}
                  compact
                />
              ) : (
                <>
              {/* Workflow Timeline */}
              <SectionCard
                tone="dashboard"
                title="Approval Journey"
                subheader="Step-by-step review chain"
                sx={panelSx}
              >
                <Stack spacing={0}>
                  {workflowSteps.map((step, idx) => (
                    <WorkflowStep
                      key={step.key}
                      step={step}
                      isLast={idx === workflowSteps.length - 1}
                    />
                  ))}
                </Stack>

                {finalStatus === 'Rejected' && (
                  <Alert severity="warning" sx={{ borderRadius: 1.5, mt: 1.5, fontSize: '0.8rem' }}>
                    Workflow stopped — a stage was rejected.
                  </Alert>
                )}
              </SectionCard>
                </>
              )}

            </Stack>
          </Box>
        </Box>

      </Box>
      </Box>

      <Dialog
        open={reviewDialog.open}
        onClose={closeReviewDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '8px' } }}
      >
        <DialogTitle>
          {reviewDialog.action === 'approved'
            ? `Approve ${reviewIsMissingPunch ? 'Missing Punch' : 'Pre-Approval'} Request`
            : reviewDialog.action === 'revised'
              ? 'Revise Pre-Approval Request'
              : `Reject ${reviewIsMissingPunch ? 'Missing Punch' : 'Pre-Approval'} Request`}
        </DialogTitle>
        <DialogContent dividers>
          {reviewRequest ? (
            <Stack spacing={2}>
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                {reviewIsMissingPunch ? (
                  <>
                    Review the submitted punch. If HR changes the time or project before approval,
                    an HR note is required.
                  </>
                ) : (
                  <>
                    Request #{reviewRequest.id} is currently at the{' '}
                    <strong>{formatStatusLabel(reviewCurrentStep, 'Current')}</strong> step.
                  </>
                )}
              </Alert>

              {reviewIsMissingPunch ? (
                <Box
                  sx={(muiTheme) => ({
                    p: 2,
                    borderRadius: '8px',
                    border: `1px solid ${alpha(muiTheme.palette.divider, 0.9)}`,
                    bgcolor: alpha(muiTheme.palette.primary.main, 0.04),
                  })}
                >
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    Submitted punch
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label={`Date: ${normalizeDisplayValue(reviewRequest.missing_punch_date)}`} />
                    <Chip label={`Time: ${normalizeDisplayValue(reviewRequest.missing_punch_time)}`} />
                    <Chip label={`Type: ${normalizeDisplayValue(reviewRequest.missing_punch_type)}`} />
                    <Chip
                      label={`Project: ${
                        getProjectLabel(submittedMissingPunchProject) ||
                        normalizeDisplayValue(reviewRequest.missing_punch_project_id)
                      }`}
                    />
                  </Stack>
                  {reviewRequest.missing_punch_reason ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                      Reason: {reviewRequest.missing_punch_reason}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              {reviewIsMissingPunch && reviewDialog.action === 'approved' ? (
                <>
                  <TextField
                    label="Effective Punch Time"
                    type="time"
                    value={reviewDialog.missing_punch_effective_time}
                    onChange={(event) =>
                      setReviewDialog((prev) => ({
                        ...prev,
                        missing_punch_effective_time: event.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    required
                    helperText="Defaults to the employee submitted time. Change it only if HR is correcting the punch."
                  />

                  <Autocomplete
                    fullWidth
                    options={missingPunchProjectOptions}
                    value={selectedMissingPunchProject}
                    loading={missingPunchProjectsLoading}
                    getOptionLabel={(option) => getProjectLabel(option)}
                    isOptionEqualToValue={(option, value) => normalizeId(option?.id) === normalizeId(value?.id)}
                    onChange={(_, option) =>
                      setReviewDialog((prev) => ({
                        ...prev,
                        missing_punch_effective_project_id: option?.id ? String(option.id) : '',
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Effective Project"
                        helperText="Defaults to the submitted project. Change it only if HR is correcting the project."
                      />
                    )}
                  />
                </>
              ) : null}

              {reviewIsMissingPunch ? (
                <TextField
                  label="HR Note"
                  value={reviewDialog.missing_punch_hr_note}
                  onChange={(event) =>
                    setReviewDialog((prev) => ({ ...prev, missing_punch_hr_note: event.target.value }))
                  }
                  multiline
                  minRows={3}
                  required={missingPunchHrNoteRequired}
                  helperText={
                    missingPunchHrNoteRequired
                      ? 'Required because the approved time or project differs from the submitted values.'
                      : reviewDialog.action === 'rejected'
                        ? 'Optional internal HR note. Rejection reason is still required below.'
                        : 'Optional if approving exactly as submitted.'
                  }
                />
              ) : null}

              {reviewIsHodApproval ? (
                <>
                  <TextField
                    label="Total Cost to Company"
                    value={normalizeDisplayValue(reviewRequest?.total_cost_to_company)}
                    InputProps={{ readOnly: true }}
                  />

                  <FormControl fullWidth required>
                    <InputLabel id="detail-pre-approval-billing-mode-label">Billing Mode</InputLabel>
                    <Select
                      labelId="detail-pre-approval-billing-mode-label"
                      label="Billing Mode"
                      value={reviewDialog.pre_approval_billing_mode}
                      onChange={(event) =>
                        setReviewDialog((prev) => ({
                          ...prev,
                          pre_approval_billing_mode: event.target.value,
                          marginal_benefit:
                            event.target.value === 'billed' ? String(reviewComputedMargin || '') : prev.marginal_benefit,
                        }))
                      }
                    >
                      {PRE_APPROVAL_BILLING_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>HOD must complete commercial inputs before approval.</FormHelperText>
                  </FormControl>

                  <TextField
                    label={reviewBillingMode === 'lumpsum' ? 'Lumpsum Amount' : 'Client Billing (No VAT)'}
                    type={reviewIsBillingHq ? 'text' : 'number'}
                    value={reviewIsBillingHq ? 'N/A' : reviewDialog.client_billing}
                    onChange={(event) =>
                      setReviewDialog((prev) => ({ ...prev, client_billing: event.target.value }))
                    }
                    inputProps={reviewIsBillingHq ? undefined : { min: 0, step: '0.01' }}
                    required={!reviewIsBillingHq}
                    InputProps={{ readOnly: reviewIsBillingHq }}
                    helperText={reviewIsBillingHq ? 'Not applicable for Billing HQ.' : undefined}
                  />

                  <TextField
                    label="Margin Benefit %"
                    type={reviewIsBillingHq ? 'text' : 'number'}
                    value={
                      reviewIsBillingHq
                        ? 'N/A'
                        : reviewBillingMode === 'billed'
                          ? reviewComputedMargin
                          : reviewDialog.marginal_benefit
                    }
                    onChange={(event) =>
                      setReviewDialog((prev) => ({ ...prev, marginal_benefit: event.target.value }))
                    }
                    inputProps={reviewIsBillingHq ? undefined : { min: 0, step: '0.01' }}
                    required={reviewBillingMode === 'lumpsum'}
                    disabled={reviewIsBillingHq || reviewBillingMode === 'billed'}
                    helperText={
                      reviewIsBillingHq
                        ? 'Not applicable for Billing HQ.'
                        : reviewBillingMode === 'billed'
                          ? 'Auto-calculated from total cost to company and client billing.'
                          : 'Required when billing mode is lumpsum.'
                    }
                  />
                </>
              ) : null}

              {reviewDialog.action === 'revised' ? (
                <FormControl fullWidth required>
                  <InputLabel id="detail-pre-approval-revision-target-label">Revision Target</InputLabel>
                  <Select
                    labelId="detail-pre-approval-revision-target-label"
                    label="Revision Target"
                    value={reviewDialog.revision_target_step}
                    onChange={(event) =>
                      setReviewDialog((prev) => ({
                        ...prev,
                        revision_target_step: event.target.value,
                      }))
                    }
                  >
                    {reviewTargetOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Revision can only go back to the creator or, after HOD, back to HOD.
                  </FormHelperText>
                </FormControl>
              ) : null}

              {reviewDialog.action !== 'approved' ? (
                <TextField
                  label={reviewDialog.action === 'revised' ? 'Revision Reason' : 'Rejection Reason'}
                  value={reviewDialog.reason}
                  onChange={(event) =>
                    setReviewDialog((prev) => ({ ...prev, reason: event.target.value }))
                  }
                  multiline
                  minRows={3}
                  required
                />
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitReviewDialog} disabled={updating}>
            {updating
              ? 'Saving...'
              : reviewDialog.action === 'approved'
                ? 'Approve'
                : reviewDialog.action === 'revised'
                  ? 'Send Revision'
                  : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

export default ShowHrRequestPage;
